import { createRoute } from "@tanstack/react-router";
import { withPermission } from "../lib/with-permission";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { X, Plus, Save, RefreshCw } from "lucide-react";
import { rootRoute } from "./__root";
import { api } from "../lib/api";
import { PageHeader } from "../components/ui/PageHeader/PageHeader";
import { Card } from "../components/ui/Card/Card";
import { Button } from "../components/ui/Button/Button";
import { Input } from "../components/ui/Input/Input";
import { FormField } from "../components/ui/FormField/FormField";
import { Badge } from "../components/ui/Badge/Badge";
import styles from "./AdminEngineBrands.module.css";

export const route = createRoute({
	getParentRoute: () => rootRoute,
	path: "/admin/engine-brands",
	component: withPermission(AdminEngineBrandsPage, "admin", "read"),
});

interface BrandRow {
	id?: string;
	name: string;
	prefix: string;
	sapBrand?: string | null;
}

function AdminEngineBrandsPage() {
	const { t } = useTranslation();
	const [brands, setBrands] = useState<BrandRow[]>([]);
	const [newName, setNewName] = useState("");
	const [newPrefix, setNewPrefix] = useState("");
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [scanning, setScanning] = useState(false);
	const [status, setStatus] = useState<{ type: "success" | "error"; msg: string } | null>(null);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const rows = await api.get<BrandRow[]>("/api/engine-brands");
				if (cancelled) return;
				setBrands(rows);
			} catch (e) {
				if (!cancelled) setStatus({ type: "error", msg: (e as Error).message });
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, []);

	const addBrand = () => {
		const name = newName.trim();
		const prefix = newPrefix.trim().toUpperCase();
		if (!name || !prefix) return;
		setBrands((prev) => [...prev, { name, prefix }]);
		setNewName("");
		setNewPrefix("");
	};

	const removeBrand = (idx: number) => {
		setBrands((prev) => prev.filter((_, i) => i !== idx));
	};

	const save = async () => {
		setSaving(true);
		setStatus(null);
		try {
			const saved = await api.put<BrandRow[]>("/api/engine-brands", { list: brands });
			setBrands(saved);
			setStatus({ type: "success", msg: t("saved") });
		} catch (e) {
			setStatus({ type: "error", msg: (e as Error).message });
		} finally {
			setSaving(false);
		}
	};

	const scanSap = async () => {
		setScanning(true);
		setStatus(null);
		try {
			const found = await api.post<BrandRow[]>("/api/engine-brands/scan-sap", {});
			setBrands((prev) => {
				const byPrefix = new Map(prev.map((b) => [b.prefix.toUpperCase(), b]));
				for (const f of found) {
					const key = (f.prefix || f.name).toUpperCase();
					if (!byPrefix.has(key)) byPrefix.set(key, f);
				}
				return [...byPrefix.values()];
			});
			setStatus({ type: "success", msg: `${found.length} ${t("brandsFromSap")}` });
		} catch (e) {
			setStatus({ type: "error", msg: (e as Error).message });
		} finally {
			setScanning(false);
		}
	};

	return (
		<div className={styles.page}>
			<PageHeader title={t("engineBrands")} subtitle={t("engineBrandsSubtitle")} />

			{status && (
				<div className={status.type === "success" ? styles.success : styles.error}>
					{status.msg}
				</div>
			)}

			<Card>
				<h2 className={styles.cardTitle}>{t("engineBrands")}</h2>
				<table className={styles.table}>
					<thead>
						<tr>
							<th>{t("brand")}</th>
							<th>PREFIX</th>
							<th />
						</tr>
					</thead>
					<tbody>
						{brands.map((b, idx) => (
							<tr key={`${b.prefix}-${idx}`}>
								<td>{b.name}</td>
								<td>
									<Badge>{b.prefix}</Badge>
								</td>
								<td className={styles.removeCell}>
									<button
										type="button"
										className={styles.removeBtn}
										onClick={() => removeBrand(idx)}
										aria-label={t("delete")}
									>
										<X size={16} />
									</button>
								</td>
							</tr>
						))}
						{!loading && brands.length === 0 && (
							<tr>
								<td colSpan={3} className={styles.empty}>
									{t("noRecords")}
								</td>
							</tr>
						)}
					</tbody>
				</table>

				<div className={styles.addRow}>
					<FormField label={t("brandName")}>
						<Input
							placeholder="ör: Perkins"
							value={newName}
							onChange={(e) => setNewName(e.target.value)}
						/>
					</FormField>
					<FormField label="Prefix">
						<Input
							placeholder="ör: GJP"
							value={newPrefix}
							onChange={(e) => setNewPrefix(e.target.value)}
						/>
					</FormField>
					<Button variant="secondary" onClick={addBrand}>
						<Plus size={16} /> {t("add")}
					</Button>
				</div>

				<div className={styles.actions}>
					<Button variant="secondary" onClick={scanSap} disabled={scanning}>
						<RefreshCw size={16} /> {scanning ? t("scanning") : t("scanFromSap")}
					</Button>
					<Button onClick={save} disabled={saving}>
						<Save size={16} /> {saving ? t("saving") : t("save")}
					</Button>
				</div>
			</Card>
		</div>
	);
}
